import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { CellValueChangedEvent } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { CostLayerUnitCostCellEditorComponent } from '@app/modules/cost-layers/components/cost-layer-unit-cost-cell-editor/cost-layer-unit-cost-cell-editor.component';
import { CostLayersAgGridColumnDef } from '@app/modules/cost-layers/config/cost-layers-basic-table.config';
import { ECostLayerField } from '@app/modules/cost-layers/cost-layers.const';
import { CostLayersApiService } from '@app/modules/cost-layers/services/cost-layers-api.service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { LumResponseError } from 'v2/shared/api/lum-response-error';

//

const _COL_DEFS :ColDef[] = [
  {
    ...CostLayersAgGridColumnDef.WAREHOUSE,
    pinned: 'left',
    sort: 'asc',
    sortIndex: 1,
  },
  { ...CostLayersAgGridColumnDef.DATE_CREATED, sort: 'desc', sortIndex: 2 },
  CostLayersAgGridColumnDef.COST_UNIT,
  CostLayersAgGridColumnDef.LOT_NUMBER,
  CostLayersAgGridColumnDef.QUANTITY_REMAINING,
  CostLayersAgGridColumnDef.QUANTITY_INITIAL,
  CostLayersAgGridColumnDef.VARIANCE,
  CostLayersAgGridColumnDef.COST_QUANTITY_REMAINING,
  { ...CostLayersAgGridColumnDef.COST_TOTAL, hide: true },
  { ...CostLayersAgGridColumnDef.DATE_UPDATED, hide: true },
];

//

@Component({
  selector: 'cnc-rfq-details-cost',
  templateUrl: './rfq-details-cost.component.html',
  styleUrls: ['./rfq-details-cost.component.scss'],
})
export class RfqDetailsCostComponent implements OnInit, OnDestroy {
  @Input() rfq;
  costLayersColumns :ColDef[] = _COL_DEFS;
  private onDestroy$ = new Subject();

  readonly #showEmptyCostLayers$ = new BehaviorSubject<boolean>(false);
  readonly showEmptyCostLayers$ = this.#showEmptyCostLayers$.asObservable();
  get showEmptyCostLayers() {
    return this.#showEmptyCostLayers$.value;
  }
  set showEmptyCostLayers(value :boolean) {
    this.#showEmptyCostLayers$.next(value);
  }

  constructor(
    private toastr :ToastrService,
    private costLayersApiService :CostLayersApiService,
    private featureToggleService :FeatureToggleService,
    private dialog :MatLegacyDialog,
  ) {}

  ngOnInit() {
    this.initializeColumnDefs();
  }

  ngOnDestroy() :void {
    this.#showEmptyCostLayers$.complete();
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private initializeColumnDefs() :void {
    this.costLayersColumns = _COL_DEFS.map((colDef) => {
      if (
        this.featureToggleService.isActive(
          'red_CS-4036_override_cost_layer_unit_cost',
        ) &&
        colDef.field === ECostLayerField.COST_UNIT
      ) {
        return {
          ...colDef,
          editable: (params) => {
            const remainingQty = params.data?.remainingQuantity || 0;
            const variance = params.data?.isVariance || false;

            if (remainingQty < 0 || variance) {
              return false;
            }

            return true;
          },
          cellEditorFramework: CostLayerUnitCostCellEditorComponent,
          onCellValueChanged: (event :CellValueChangedEvent) => {
            if (event.data._suppressCellValueChanged) {
              delete event.data._suppressCellValueChanged;

              return;
            }

            const oldValue = event.oldValue;
            const newValue = event.newValue;

            if (oldValue === newValue) {
              return;
            }

            this.openUnitCostChangeConfirmationDialog(event);
          },
        };
      }

      return colDef;
    });
  }

  private openUnitCostChangeConfirmationDialog(event :CellValueChangedEvent) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Update',
          message: `Are you sure you want to update the unit cost?`,
          confirmButton: 'Yes',
          cancelButton: 'No',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed :boolean) => {
        if (confirmed) {
          this.costLayersApiService
            .updateCostLayerUnitCost({costLayerId: event.data.id, costPerUnit: event.newValue, relations: 'lot'})
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (resp) => {
                this.toastr.success('Unit cost updated successfully', 'Success');
                event.node.setData({
                  ...resp,
                  warehouse: event.data.warehouse,
                });
              },
              error: (error :LumResponseError) => {
                this.toastr.error(error.message, 'Error');
                event.data._suppressCellValueChanged = true;
                event.node.setDataValue(event.colDef.field, event.oldValue);
              },
            });
        } else {
          event.data._suppressCellValueChanged = true;
          event.node.setDataValue(event.colDef.field, event.oldValue);
        }
      });
  }
}
